/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type {
  BodyScrollEvent,
  IRowNode,
  PostSortRowsParams,
  CellValueChangedEvent,
  GridApi,
  GridReadyEvent,
  ModelUpdatedEvent,
  RowClassParams,
} from 'ag-grid-community'
import { useEffect, useRef, useState } from 'react'

import colors from '@sportninja/common/constants/appColors'
import type { BulkAction } from '@sportninja/common/features/game/gameListView/bulkPlan'
import type { OfficialAssignment } from '@sportninja/common/features/game/gameListView/officialBuckets'
import type {
  TeamRef,
  VenueRef,
} from '@sportninja/common/features/game/gameListView/types'

import DataGrid from 'src/components/ts-components/DataGrid'

import type { OfficialTypeOption } from './OfficialsCellEditor'
import type { OfficialOption } from '../hooks/useOfficialOptions'
import {
  FACILITY_UNASSIGNED_VALUE,
  useDetailsColumns,
} from '../hooks/useDetailsColumns'
import type { GameTypeOption } from '../hooks/useDetailsColumns'
import { TBD_CELL_VALUE } from '../utils/gameGridRows'
import type { GameGridRow } from '../utils/gameGridRows'
import { gameViewsLabel } from '../utils/i18n'
import { decodeVenueCommit } from '../utils/venueCommit'

/**
 * Fixed height, not `domLayout="autoHeight"`.
 *
 * autoHeight renders every row into the DOM, which is fine for a team roster and
 * ruinous here: a season view is hundreds of rows, and materialising them all
 * throws away the virtualization that keeps scrolling and selection responsive.
 */
const GRID_HEIGHT = 620

/** Roomy enough to read a date, a time and two team names without squinting. */
const ROW_HEIGHT = 48
const HEADER_HEIGHT = 44

/**
 * How close to the bottom of the grid's own viewport counts as "asking for
 * more". The List view has always used 500px against the document scroller;
 * this is the same idea against the grid's internal one.
 */
const SCROLL_THRESHOLD = 500

export interface DetailsGridProps {
  rows: GameGridRow[]
  venuesById: Map<string, VenueRef>
  /** Every selectable team, keyed by uid — the Home / Visitor editors read it. */
  teamsById: Map<string, TeamRef>
  /** True while the team pool is in flight, so the editor can say so. */
  teamsLoading: boolean
  gameTypes: GameTypeOption[]
  canEdit: boolean
  showTimeZoneInHeader: boolean
  /** Every assignable official, unioned with the ones on loaded games. */
  officials: OfficialOption[]
  /** uid → display name, for the officials columns' labels and filters. */
  officialNamesById: Map<string, string>
  /** Every official role. Never filtered by sport — see `useDetailsColumns`. */
  officialTypes: OfficialTypeOption[]
  /** False where the officials pool cannot be scoped; the columns go read-only. */
  canAssignOfficials: boolean
  loading: boolean
  /** Suppressed while a run summary is up so rows cannot move underneath it. */
  suppressReordering: boolean
  /**
   * Take whatever height the parent has left instead of the fixed default.
   *
   * Only ever true inside the full-screen shell, which is the one place the
   * grid has a parent of known height to fill. Everywhere else the panel is in
   * normal page flow with no height of its own, and `flex: 1` against that
   * collapses the grid to nothing.
   */
  fillHeight?: boolean
  /**
   * Where popup editors are mounted, overriding the body default.
   *
   * The full-screen shell has to pass itself here. Body-parented popups are
   * measured and CLAMPED against the document — which, once the panel is
   * `position: fixed`, no longer describes anything the user can see: the flow
   * content behind collapses to a few hundred pixels, so AG Grid drags every
   * panel back inside that phantom box, and the shell's own opaque layer covers
   * whatever is left. Parented to the shell, the same maths reads the viewport
   * and the popups land under their cells.
   */
  popupParent?: HTMLElement | null
  onSelectionChanged: (rows: GameGridRow[]) => void
  onCellAction: (row: GameGridRow, action: BulkAction) => void
  /** Opens the full add/edit Game modal for one row. */
  onEditGame?: (row: GameGridRow) => void
  /** Fired when the user scrolls near the bottom, to pull the next page. */
  onReachBottom?: () => void
  /** Hands the grid API up so the toolbar can search, hide columns and export. */
  onGridApiReady?: (api: GridApi) => void
  /** What the grid is actually showing, after filters and search. */
  onDisplayedRowsChanged?: (status: {
    displayed: number
    isFiltered: boolean
  }) => void
}

/*
 * Where popup editors are mounted. Read once — `document` is always present in
 * this app (CRA, no SSR), and re-reading it per render would hand AG Grid a
 * "changed" grid option on every pass.
 */
const POPUP_PARENT = typeof document === 'undefined' ? null : document.body

const DetailsGrid = ({
  rows,
  venuesById,
  teamsById,
  teamsLoading,
  gameTypes,
  canEdit,
  showTimeZoneInHeader,
  officials,
  officialNamesById,
  officialTypes,
  canAssignOfficials,
  loading,
  suppressReordering,
  fillHeight = false,
  popupParent,
  onSelectionChanged,
  onCellAction,
  onEditGame,
  onReachBottom,
  onGridApiReady,
  onDisplayedRowsChanged,
}: DetailsGridProps) => {
  'use memo'

  const apiRef = useRef<GridReadyEvent['api'] | null>(null)

  /*
   * The row order the user last saw, kept up to date while nothing is running.
   *
   * `suppressModelUpdateAfterUpdateTransaction` does NOT cover this: it only
   * applies to `applyTransaction` updates, and this grid receives new `rowData`
   * from React on every optimistic patch, which AG Grid treats as a full model
   * replacement and re-sorts. With an active DATE sort, retiming the top row
   * threw it to the bottom mid-review.
   *
   * Captured continuously rather than at run start, because the optimistic
   * patch can land before a "run started" re-render does.
   */
  const stableOrderRef = useRef<string[]>([])

  const captureOrder = (api: GridReadyEvent['api'] | null) => {
    if (api == null || suppressReordering) {
      return
    }
    const ids: string[] = []
    api.forEachNodeAfterFilterAndSort((node: IRowNode<GameGridRow>) => {
      if (node.data !== undefined) {
        ids.push(node.data.id)
      }
    })
    stableOrderRef.current = ids
  }

  /**
   * Reimposes the frozen order after AG Grid sorts.
   *
   * Rows the frozen order has never seen (a page loaded mid-run) fall to the
   * end rather than being dropped.
   */
  //  When the freeze lifts, the model has to be re-sorted or the frozen order
  //  simply persists — the review is over, so canonical order comes back.
  const wasSuppressedRef = useRef(suppressReordering)
  useEffect(() => {
    if (wasSuppressedRef.current && !suppressReordering) {
      apiRef.current?.refreshClientSideRowModel('sort')
    }
    wasSuppressedRef.current = suppressReordering
  }, [suppressReordering])

  const postSortRows = (params: PostSortRowsParams<GameGridRow>) => {
    if (!suppressReordering || stableOrderRef.current.length === 0) {
      return
    }
    const rank = new Map(stableOrderRef.current.map((id, index) => [id, index]))
    params.nodes.sort((a, b) => {
      const left = rank.get(a.data?.id ?? '') ?? Number.MAX_SAFE_INTEGER
      const right = rank.get(b.data?.id ?? '') ?? Number.MAX_SAFE_INTEGER
      return left - right
    })
  }

  /*
   * Which column the grid is sorted by, so the sheet can tint it end to end the
   * way the Statistics table does. AG Grid marks the sorted HEADER for us
   * (`ag-header-cell-sorted-asc`) but says nothing on the body cells, and the
   * tint is most of the signal — on a 10-column sheet a caret in one header is
   * easy to miss.
   *
   * CSS keyed to `col-id` rather than a `cellClass`: a class would have to be
   * re-evaluated on every sort with a forced `refreshCells`, while a stylesheet
   * rule catches cells as they virtualize in, for free.
   */
  const [sortedColId, setSortedColId] = useState<string>('')

  const captureSortedColumn = (api: GridReadyEvent['api']) => {
    const sorted = api
      .getColumnState()
      .find((column) => column.sort !== null && column.sort !== undefined)
    setSortedColId(sorted?.colId ?? '')
  }

  const columnDefs = useDetailsColumns({
    canEdit,
    venuesById,
    teamsById,
    teamsLoading,
    gameTypes,
    showTimeZoneInHeader,
    isRowPending: (row) => row.isPending,
    officials,
    officialNamesById,
    officialTypes,
    canAssignOfficials,
  })

  /**
   * Translates one committed cell into the same `BulkAction` the toolbar emits,
   * so a single edit and a ten-row bulk edit go through identical planning,
   * validation and reconciliation. There is no second, weaker code path.
   */
  /*
   * Puts a rejected edit back, without the restore itself looking like an edit.
   *
   * `setDataValue` fires `cellValueChanged` again, which would otherwise be
   * dispatched as a real change — and, for a value that lands in this same
   * branch, would recurse. The flag makes the echo a no-op.
   */
  const restoringRef = useRef(false)

  const restoreValue = (
    event: CellValueChangedEvent<GameGridRow>,
    colId: string,
    oldValue: unknown
  ) => {
    restoringRef.current = true
    try {
      event.node.setDataValue(colId, oldValue)
    } finally {
      restoringRef.current = false
    }
  }

  const handleCellValueChanged = (
    event: CellValueChangedEvent<GameGridRow>
  ) => {
    const row = event.data
    if (row === undefined || restoringRef.current) {
      return
    }

    const colId = event.column.getColId()
    const value = event.newValue

    switch (colId) {
      case 'localDate':
        onCellAction(row, { kind: 'setLocalDate', date: String(value ?? '') })
        break
      case 'localTime':
        /*
         * The marker means "take the time away", not a time. No `restoreValue`
         * branch, unlike `facilityId`: both outcomes are expressible against
         * the API, so there is nothing to put back.
         */
        onCellAction(
          row,
          value === TBD_CELL_VALUE
            ? { kind: 'setTbd' }
            : { kind: 'setLocalTime', time: String(value ?? '') }
        )
        break
      case 'venueId': {
        const commit = decodeVenueCommit(value)
        if (commit === null) {
          return
        }
        onCellAction(row, { kind: 'setVenue', ...commit })
        break
      }
      case 'facilityId':
        if (value === FACILITY_UNASSIGNED_VALUE || value == null) {
          /*
           * Null cannot clear a facility through this API, so there is nothing
           * honest to send. `refreshCells` alone did NOT undo this: AG Grid has
           * already written the new value into the row, so re-rendering just
           * redrew "Unassigned" on a row nothing was saved for. Put the old
           * value back.
           */
          restoreValue(event, 'facilityId', event.oldValue)
          return
        }
        onCellAction(row, { kind: 'setFacility', facilityId: String(value) })
        break
      /*
       * The cell value is the team's UID, and an empty one is not expressible:
       * the API drops a submitted null, so a game cannot have a side cleared.
       * The editor never commits one — this guards the path anyway rather than
       * sending a patch that would silently do nothing.
       */
      case 'homeLabel':
        if (value == null || value === '') {
          return
        }
        onCellAction(row, { kind: 'setHomeTeam', teamId: String(value) })
        break
      case 'awayLabel':
        if (value == null || value === '') {
          return
        }
        onCellAction(row, { kind: 'setVisitingTeam', teamId: String(value) })
        break
      case 'gameNumber':
        onCellAction(row, { kind: 'setGameNumber', value: String(value ?? '') })
        break
      case 'gameTypeId':
        onCellAction(row, {
          kind: 'setGameType',
          gameTypeId: value == null ? null : Number(value),
        })
        break
      case 'referees':
      case 'scorekeepers':
      case 'timekeepers':
        //  The cell value IS the assignment list — the editor commits the array
        //  rather than an encoded string, because this is one column's value.
        onCellAction(row, {
          kind: 'setOfficials',
          bucket: colId,
          assignments: (value as OfficialAssignment[] | null) ?? [],
        })
        break
      default:
        break
    }
  }

  /**
   * AG Grid virtualizes inside its own viewport, so the document scroll handler
   * the List view uses never fires here. This is the same behaviour, wired to
   * the scroller that actually moves.
   */
  const handleBodyScroll = (event: BodyScrollEvent) => {
    if (onReachBottom === undefined || event.direction !== 'vertical') {
      return
    }
    const viewport = event.api.getVerticalPixelRange()
    const contentHeight = rows.length * ROW_HEIGHT
    if (contentHeight - viewport.bottom < SCROLL_THRESHOLD) {
      onReachBottom()
    }
  }

  /**
   * Fires after every sort, filter, search and row-data patch.
   *
   * It reports what is on screen, because the status strip has to count the
   * DISPLAYED rows rather than the loaded ones.
   */
  /*
   * Reported ONLY when the numbers actually change.
   *
   * Firing a fresh object on every model update is a render loop waiting for a
   * parent that stores it: the setState re-renders the grid, the re-render is
   * another model update, and that reports again. `GridStatusBar`'s host does
   * exactly that, and the loop never settled — `act()` simply never returned.
   */
  const lastStatusRef = useRef<{ displayed: number; isFiltered: boolean }>({
    displayed: -1,
    isFiltered: false,
  })

  const handleModelUpdated = (event: ModelUpdatedEvent<GameGridRow>) => {
    captureOrder(event.api)

    const next = {
      displayed: event.api.getDisplayedRowCount(),
      isFiltered: event.api.isAnyFilterPresent(),
    }
    const last = lastStatusRef.current
    if (
      next.displayed === last.displayed &&
      next.isFiltered === last.isFiltered
    ) {
      return
    }
    lastStatusRef.current = next
    onDisplayedRowsChanged?.(next)
  }

  const rowClass = (params: RowClassParams<GameGridRow>) => {
    const classes: string[] = []
    if (params.data?.isPending) {
      classes.push('sn-row-pending')
    }
    if (params.data?.movedOutsideFilter) {
      classes.push('sn-row-moved-outside')
    }
    if (params.data?.failure?.outcome === 'rejected') {
      classes.push('sn-row-rejected')
    }
    if (params.data?.failure?.outcome === 'conflict') {
      classes.push('sn-row-conflict')
    }
    if (params.data?.failure?.outcome === 'unconfirmed') {
      classes.push('sn-row-unconfirmed')
    }
    return classes.join(' ')
  }

  return (
    <div
      css={[
        wrapperStyles,
        fillHeight ? fillHeightStyles : fixedHeightStyles,
        sortedColumnStyles(sortedColId),
      ]}
      data-testid='game-details-grid'
      //  The tint's input, in the DOM, so what is highlighted is inspectable
      //  rather than buried in a generated class name.
      data-sorted-col-id={sortedColId}
    >
      <DataGrid<GameGridRow>
        rowData={rows}
        columnDefs={columnDefs}
        //  How the edit-button cell reaches its handler: AG Grid passes
        //  `context` to every renderer, so the callback does not have to be
        //  baked into the column defs (which would rebuild them on each render).
        context={{ onEditGame }}
        loading={loading}
        //  Stable identity across row-data patches. Without it AG Grid rebuilds
        //  every node on each patch and drops selection and focus with them —
        //  exactly the "lose your spot" the feature exists to fix.
        getRowId={({ data }) => data.id}
        /*
         * Popup editors render into the BODY, not into the grid.
         *
         * By default AG Grid appends them inside its own root, where the grid's
         * bounds clip them: opening the officials editor on a bottom row cut the
         * panel off at the last visible line, hiding its role rows and buttons
         * behind the footer. Nothing about z-index fixes that — a clipped
         * element is clipped — so the popup has to live outside the container
         * that is doing the clipping.
         */
        popupParent={popupParent ?? POPUP_PARENT}
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        // AG Grid v36 takes selection as an object; `enableClickSelection: false`
        // keeps a click on a cell from wiping a multi-row selection.
        rowSelection={
          canEdit
            ? {
                mode: 'multiRow',
                selectAll: 'filtered',
                enableClickSelection: false,
              }
            : undefined
        }
        //  The checkbox belongs to the row, not to a field, so it lives in the
        //  same pinned gutter as the Open link. Left unpinned it scrolls away
        //  with the data columns, and a sheet scrolled sideways loses the way
        //  to select the row it is showing.
        selectionColumnDef={{ pinned: 'left' }}
        postSortRows={postSortRows}
        onSortChanged={(event) => {
          captureOrder(event.api)
          captureSortedColumn(event.api)
        }}
        //  Double-click, Enter, or just start typing on a focused cell — the
        //  spreadsheet convention, and what AG Grid's own reference grid uses.
        //  `singleClickEdit` made every click to select a row open an editor.
        stopEditingWhenCellsLoseFocus
        /*
         * NOT `undoRedoCellEditing`. The reference grid owns its row data, so
         * AG Grid can replay edits off its own stack. Here every commit patches
         * the React Query cache and hands the grid new `rowData`, which CLEARS
         * that stack — so Ctrl+Z would be a shortcut that visibly does nothing,
         * which is worse than no shortcut at all when the user is trying to work
         * out whether their edit stuck. Undoing a saved change means writing the
         * old value back, and that is a server round-trip, not a grid feature.
         */
        /*
         * NO per-column filters.
         *
         * The reference grid puts a filter input and a funnel under every
         * header; nothing else in this app filters that way — every table
         * narrows from one search box above it, and the toolbar has one. Two
         * filtering models on one screen is the inconsistency, and the header
         * row it cost was 40px of chrome on a sheet that wants rows.
         *
         * The columns' `filterValueGetter`s stay load-bearing: the quick filter
         * reads through them, which is what lets a search match "Community
         * Arena" on a column that stores a venue uid.
         *
         * A changed cell still flashes so a bulk run is visible as it lands —
         * but the flag is set PER COLUMN by `withChangeFlash`, and deliberately
         * not through `defaultColDef`. That reaches the value-less gutter
         * columns too (`openGame`, `editGame`, and AG Grid's own selection
         * column), and a column with no value force-refreshes on every
         * row-data replacement — so the whole left-hand gutter lit up green
         * after any single-cell edit. No `defaultColDef` here at all now; the
         * shared `DataGrid` supplies the sortable/resizable base.
         */
        //  Keyboard-first, like any spreadsheet: Tab and the arrow keys move the
        //  focused cell, and Enter opens the editor on it.
        enterNavigatesVertically
        enterNavigatesVerticallyAfterEdit
        //  Long team and location names truncate in a 160px cell. AG Grid's
        //  default two-second delay is long enough that nobody discovers the
        //  tooltip exists.
        tooltipShowDelay={500}
        onGridReady={(event) => {
          apiRef.current = event.api
          onGridApiReady?.(event.api)
        }}
        onModelUpdated={handleModelUpdated}
        onSelectionChanged={(event) =>
          onSelectionChanged(event.api.getSelectedRows() as GameGridRow[])
        }
        onCellValueChanged={handleCellValueChanged}
        onBodyScroll={handleBodyScroll}
        getRowClass={rowClass}
        //  Params, not `overlayNoRowsTemplate`: DataGrid supplies a
        //  `noRowsOverlayComponent`, and a component always wins over the
        //  template — which is why this message was silently dead.
        noRowsOverlayComponentParams={{
          message: gameViewsLabel('gameViewNoGames'),
        }}
      />
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

/**
 * Paints the sorted column end to end — header and every cell under it — the
 * way the Statistics table paints its sorted column: `INFO_500` dodged over the
 * surface the row already sits on.
 *
 * `color-dodge` blends an element's own background layers, NOT the parent's,
 * and an AG Grid cell is transparent over its row. So each rule restates the
 * row colour it is blending over: the base surface, and `SECONDARY_400` for the
 * zebra rows.
 *
 * Painting the CELL means the sorted column keeps its tint under the row hover
 * rather than washing out with the rest of the row — which is also what the
 * Statistics table does, for the same reason: the tint is the answer to "which
 * column am I sorted by", and a hover should not be able to hide it.
 */
const sortedColumnStyles = (colId: string) => {
  if (colId === '') {
    return undefined
  }

  return css`
    .ag-header-cell[col-id='${colId}'],
    .ag-cell[col-id='${colId}'] {
      background-color: ${colors.SECONDARY_300};
      background-image: linear-gradient(${colors.INFO_500}, ${colors.INFO_500});
      background-blend-mode: color-dodge;
    }

    .ag-row-odd .ag-cell[col-id='${colId}'] {
      background-color: ${colors.SECONDARY_400};
    }
  `
}

/** The default: a fixed box in normal page flow. */
const fixedHeightStyles = css`
  height: ${GRID_HEIGHT}px;
`

/**
 * Full screen: eat the rest of the shell.
 *
 * `min-height: 0` is load-bearing — a flex item's default `min-height: auto`
 * refuses to shrink below its content, and AG Grid's content is every row, so
 * without it the grid pushes the status bar and the footer off the bottom of
 * the window.
 */
const fillHeightStyles = css`
  flex: 1 1 auto;
  min-height: 0;
`

const wrapperStyles = css`
  width: 100%;
  min-width: 0;

  /*
   * Deliberately NO row / header / hover / selection repainting here.
   *
   * The house look — SECONDARY_300 row panels separated by thick page-coloured
   * gutters, a BRIGHTER hover, an accent-tinted selected row — belongs to the
   * shared dataGridTheme, and every grid in the app has to agree on it. The
   * overrides that used to sit here fought that theme and lost on every count:
   * hover painted SECONDARY_400, DARKER than the SECONDARY_300 row it was meant
   * to lift; and both the selected-row and editable-cell tints resolved to
   * SECONDARY_300 — the row colour itself — so neither state was visible at all.
   * Only what is genuinely specific to THIS grid stays below.
   */

  /*
   * A visible focused cell is what makes the grid navigable by keyboard and
   * what tells the admin where an edit will land. Heavier than the theme's own
   * accent ring because this is the one grid in the app you drive from the
   * keyboard for minutes at a stretch.
   */
  .ag-cell-focus:not(.ag-cell-range-selected) {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: -2px;
  }

  /*
   * ...but NOT while that cell is being edited, and neither does AG Grid.
   *
   * An editing cell was carrying THREE accent rectangles nested inside each
   * other: the focus outline above, AG Grid's own cell-editing border,
   * and the field's. The field's wins — it is the one that tracks the control
   * you are actually typing in, and it is what every other date and time field
   * in the app shows. The !important is because AG Grid sets its border so.
   */
  .ag-cell-focus.ag-cell-inline-editing {
    outline: none;
  }

  .ag-cell-inline-editing {
    border: none !important;
    box-shadow: none;
  }

  /*
   * An editable cell should look editable before you click it.
   *
   * One step ABOVE the theme's row hover (SECONDARY_200) rather than level with
   * it: the row underneath is already hovered by the time the pointer reaches
   * the cell, so matching that colour would be no cue at all — which is exactly
   * how the old SECONDARY_300 value failed against the row.
   */
  .ag-cell:not(.ag-cell-not-inline-editing)[aria-readonly='false']:hover {
    background-color: ${colors.SECONDARY_100};
    cursor: text;
  }

  /*
   * The shared DataGrid renders its own wrapper div, which is width-only — every
   * other grid in the app uses autoHeight and never needs a height. Fixed-height
   * virtualization does: without this the AG Grid root collapses to 2px and the
   * rows render into a box nobody can see.
   */
  > div,
  .ag-root-wrapper {
    height: 100%;
  }

  /*
   * The leading gutter is two controls for the ROW — select it, open it — so
   * they read as a pair rather than as two columns. AG Grid pads every cell for
   * text; neither of these holds any, and the padding is the whole gap.
   */
  .ag-cell.sn-cell-open-game,
  .ag-cell[col-id='ag-Grid-SelectionColumn'],
  .ag-header-cell[col-id='ag-Grid-SelectionColumn'] {
    padding-left: 0;
    padding-right: 0;
  }

  .ag-cell.sn-cell-open-game,
  .ag-cell[col-id='ag-Grid-SelectionColumn'] {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /*
   * ...and the select-all with them. AG Grid lays the header checkbox out
   * FLUSH LEFT and then puts an (empty, because the column is headerless) label
   * wrapper beside it, which is what left the header box sitting 11px left of
   * every checkbox under it.
   */
  .ag-header-cell[col-id='ag-Grid-SelectionColumn']
    .ag-header-cell-comp-wrapper {
    display: none;
  }

  .ag-header-cell[col-id='ag-Grid-SelectionColumn'] .ag-header-select-all {
    margin-left: auto;
    margin-right: auto;
  }

  /*
   * The row checkbox carries a 12px right margin for the label that would
   * follow it in a normal cell. There is no label here, so the margin was pure
   * offset — 6px left of the centred header box above it.
   */
  .ag-cell[col-id='ag-Grid-SelectionColumn'] .ag-selection-checkbox {
    margin-right: 0;
  }

  .sn-row-pending {
    opacity: 0.6;
  }
  .sn-row-moved-outside .ag-cell:first-of-type {
    box-shadow: inset 3px 0 0 ${colors.PRIMARY};
  }
  .sn-row-rejected .ag-cell:first-of-type {
    box-shadow: inset 3px 0 0 ${colors.ERROR_200};
  }
  .sn-row-conflict .ag-cell:first-of-type,
  .sn-row-unconfirmed .ag-cell:first-of-type {
    box-shadow: inset 3px 0 0 ${colors.WARNING_200};
  }
`

export default DetailsGrid
